import { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Save, Trash2, History } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { preInscricoesApi } from '../../lib/api';
import logger from '../../lib/logger';
import { STATUS_LABELS, StatusBadge } from './PreInscricoesStats';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-');
const fmtPhone = (d = '') => (d.length === 11 ? `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}` : d.length === 10 ? `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}` : d);

export const PreInscricaoDetailsDialog = ({ item, open, onOpenChange, onChanged, onDeleted }) => {
  const [status, setStatus] = useState('novo');
  const [obs, setObs] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (item) { setStatus(item.status || 'novo'); setObs(item.observacoes_internas || ''); }
  }, [item, open]);

  if (!item) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (status !== item.status) payload.status = status;
      if (obs !== (item.observacoes_internas || '')) payload.observacoes_internas = obs;
      if (!Object.keys(payload).length) { toast.info('Nenhuma alteração para salvar'); return; }
      const res = await preInscricoesApi.update(item.pre_inscricao_id, payload);
      toast.success('Pré-inscrição atualizada');
      onChanged?.(res.data);
    } catch (e) {
      logger.error(e);
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await preInscricoesApi.delete(item.pre_inscricao_id);
      toast.success('Pré-inscrição excluída (LGPD)');
      setConfirmDelete(false);
      onDeleted?.(item.pre_inscricao_id);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-obsidian-200 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pre-inscricao-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{item.nome_participante_1} e {item.nome_participante_2}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">Pré-inscrição • edição {item.edicao} • <StatusBadge status={item.status} /></DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <p className="flex items-center gap-2 text-gray-300"><Phone className="w-4 h-4 text-kairos-orange" /><a className="hover:underline" href={`https://wa.me/55${item.telefone}`} target="_blank" rel="noopener noreferrer" data-testid="details-whatsapp-link">{fmtPhone(item.telefone)}</a></p>
            <p className="flex items-center gap-2 text-gray-300"><Mail className="w-4 h-4 text-kairos-orange" />{item.email}</p>
            <p className="flex items-center gap-2 text-gray-300"><MapPin className="w-4 h-4 text-kairos-orange" />{item.cidade}/{item.estado}</p>
            <p className="flex items-center gap-2 text-gray-300"><Clock className="w-4 h-4 text-kairos-orange" />{fmt(item.criado_em)}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-obsidian-50 rounded-lg text-xs">
            <div><p className="text-gray-500">Já participou</p><p className="text-white">{item.ja_participou ? 'Sim' : 'Não'}</p></div>
            <div><p className="text-gray-500">Como conheceu</p><p className="text-white">{item.como_conheceu || '-'}</p></div>
            <div><p className="text-gray-500">Origem</p><p className="text-white">{item.utm?.utm_source || item.origem || 'site'}{item.utm?.utm_campaign ? ` / ${item.utm.utm_campaign}` : ''}</p></div>
            <div><p className="text-gray-500">Comunicações</p><p className={item.aceite_comunicacoes ? 'text-green-400' : 'text-gray-400'}>{item.aceite_comunicacoes ? 'Autorizado' : 'Não autorizado'}</p></div>
          </div>

          <div className="space-y-3">
            <label className="text-gray-400 text-sm block">Status administrativo</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-11 rounded-md bg-obsidian-50 border border-white/10 text-white px-3 text-sm" data-testid="details-status-select">
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <label className="text-gray-400 text-sm block">Observações internas</label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3} className="bg-obsidian-50 border-white/10 text-white" placeholder="Anotações da equipe (não visíveis ao casal)" data-testid="details-obs-textarea" />
            <div className="flex justify-between gap-2">
              <Button variant="ghost" onClick={() => setConfirmDelete(true)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="details-delete-btn"><Trash2 className="w-4 h-4 mr-2" />Excluir (LGPD)</Button>
              <Button onClick={save} disabled={saving} className="bg-kairos-orange hover:bg-kairos-orange-dark text-white" data-testid="details-save-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Salvando...' : 'Salvar'}</Button>
            </div>
          </div>

          <div>
            <p className="text-gray-400 text-sm flex items-center gap-2 mb-2"><History className="w-4 h-4" />Histórico</p>
            <ul className="space-y-1.5 max-h-40 overflow-y-auto text-xs" data-testid="details-historico">
              {(item.historico || []).slice().reverse().map((h, i) => (
                <li key={i} className="text-gray-400 border-l-2 border-white/10 pl-3">
                  <span className="text-gray-500">{fmt(h.em)}</span> — <span className="text-gray-300">{h.acao.replace(/_/g, ' ')}</span>{h.por ? ` por ${h.por}` : ''}
                  {h.alteracoes && <span className="block text-gray-500">{Object.entries(h.alteracoes).map(([k, v]) => `${k}: ${v.de ?? '-'} → ${v.para}`).join(' • ')}</span>}
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-obsidian-200 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir pré-inscrição?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Os dados de {item.nome_participante_1} e {item.nome_participante_2} serão removidos permanentemente (atende solicitações de exclusão LGPD). A ação ficará registrada no log administrativo.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700 text-white" data-testid="details-confirm-delete-btn">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
